import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { PartnerStatus } from 'clickNgo/entities/partner/configs'
import { shouldCopyAddress } from 'clickNgo/entities/partner/libs/helpers'
import { IPartner } from 'clickNgo/entities/partner/models'
import { useGetFormData } from 'cng-common/features/form'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'

import { DEFAULT_PARTNER_DATA_FORM_SETTINGS, PartnerDataFormKeys } from '../configs'
import { IPartnerDataFormData, IPartnerDataFormSettings } from '../models'

interface IUseInitPartnerDataFormSettingArgs extends IPartnerDataFormData {
  isDisabled?: boolean
  onboardingRequestId?: number
  source: string
}

export const useInitPartnerEditorSetting = ({
  partner,
  partnerAttachments,
  partnerFlags,
  isDisabled,
  onboardingRequestId,
  source,
}: IUseInitPartnerDataFormSettingArgs) => {
  const user = useSelector(selectUser)

  const partnerData = useMemo<IPartner>(() => {
    return {
      ...partner,
      onboardingRequestId: onboardingRequestId || null,
      source,
      sourceRequestor: user.email,
    }
  }, [onboardingRequestId, source, user.email, partner])

  const defaultSettings = useMemo(() => {
    const isInactive = partner.status === PartnerStatus.Inactive
    const isReadOnly = isDisabled || isInactive
    const doCopyBasicAddress = shouldCopyAddress(partnerData)

    return {
      ...DEFAULT_PARTNER_DATA_FORM_SETTINGS,
      actualData: {
        partner: partnerData,
        partnerFlags,
        partnerAttachments,
      },
      isReadOnly,
      isInactive,
      doCopyBasicAddress,
    } as IPartnerDataFormSettings
  }, [partner, partnerFlags, partnerAttachments, isDisabled, partnerData])

  const [settings] = useGetFormData<IPartnerDataFormSettings>(
    PartnerDataFormKeys.Setting,
    defaultSettings
  )

  return settings.formData
}
